/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React, { type ReactNode } from 'react';

import { useHistory } from 'react-router';

import { ListeTable, ListeTableContainer } from '@mairistem/liste';

import type { Pacs } from '@/types/Pacs';
import { AssistantContext } from '@/contexts';
import { StatutPacs } from '@/constants';
import { canDeclarerSurConvention, getFichePacsPath } from '@/utils';
import PacsListeTableRow from './PacsListeTableRow';
import DeclarationNonEnregistreeModal from './DeclarationNonEnregistreeModal';

interface PacsListeTableProps {
  items: Array<Partial<Pacs>>;
  count: number;
  mode?: string;
  perPage: number;
  loading: boolean;
  dashboard: boolean;
  modal?: boolean;
  stats?: boolean;
  assistant?: boolean;
  'data-testid'?: string;
  onLoad: any;
  onSort: any;
  onSelect?: (item: Partial<Pacs>) => void;
}

const PacsListeTable = (props: PacsListeTableProps) => {
  const {
    items,
    mode,
    count,
    perPage,
    loading,
    dashboard,
    modal,
    stats,
    assistant,
    'data-testid': dataTestId = 'pacs-liste',
    onLoad,
    onSort,
    onSelect: onSelectProp,
  } = props;

  const history = useHistory();
  const [selectedItemId, setSelectedItemId] = React.useState<number | null>(
    null,
  );

  const assistantContext = React.useContext(AssistantContext);
  const selectedDeclarationType = assistantContext?.selectedDeclarationType;

  const [pacsNonEnregistre, setPacsNonEnregistre] = React.useState<any>(null);

  const getConvention = React.useCallback(
    (item: any) => (stats ? item : item?.derniereConvention),
    [stats],
  );

  const getPacsId = React.useCallback(
    (item: any) => {
      if (stats) {
        return item?.pacs?.identifiant;
      }
      if (modal) {
        return item?.identifiantPacs;
      }
      return item?.identifiant;
    },
    [stats, modal],
  );

  const getFichePath = React.useCallback(
    (item: any) =>
      getFichePacsPath(
        getPacsId(item),
        getConvention(item)?.statut === StatutPacs.PROJET,
      ),
    [getPacsId, getConvention],
  );

  const renderRow = (item, invalidate) => (
    <PacsListeTableRow
      item={item}
      mode={mode}
      invalidate={invalidate}
      dashboard={dashboard}
      modal={modal}
      stats={stats}
      assistant={assistant}
      selected={selectedItemId === item?.identifiant}
      data-testid={dataTestId}
    />
  );

  const handleSelect = (selectedItem) => {
    // Contrôle métier : une modificative / dissolution ne peut être établie que
    // sur une déclaration de PACS enregistrée.
    // Si pas enregistré : ligne grisé + modal préventive si convention cliqué
    if (
      !selectedItem?.isAssistantEmptyRow &&
      !canDeclarerSurConvention(
        selectedDeclarationType,
        getConvention(selectedItem),
      )
    ) {
      setPacsNonEnregistre(selectedItem);
      return;
    }

    if (onSelectProp) {
      setSelectedItemId(selectedItem?.identifiant);
      onSelectProp(selectedItem);
    } else {
      history.push(getFichePath(selectedItem));
    }
  };

  const handleOpenFichePacs = React.useCallback(() => {
    const pacsId = getPacsId(pacsNonEnregistre);
    setPacsNonEnregistre(null);

    if (_.isNil(pacsId)) {
      return;
    }

    history.push(getFichePath(pacsNonEnregistre));
  }, [getPacsId, getFichePath, history, pacsNonEnregistre]);

  const renderAssistantHeader = React.useMemo(
    () => (
      <>
        <ListeTable.Column name="annee" sortable width={1}>
          Année
        </ListeTable.Column>
        <ListeTable.Column name="registre" sortable width={2}>
          Registre
        </ListeTable.Column>
        <ListeTable.Column name="partenaire1" sortable width={4}>
          Partenaire 1
        </ListeTable.Column>
        <ListeTable.Column name="partenaire2" sortable width={4}>
          Partenaire 2
        </ListeTable.Column>
      </>
    ),
    [],
  );

  const renderDashboardHeader = React.useMemo(
    () => (
      <>
        <ListeTable.Column
          name="partenaires"
          style={{ maxWidth: '10em' }}
          width={5}
        >
          Nom des partenaires
        </ListeTable.Column>
        <ListeTable.Column name="typePacs" width={1}>
          Type
        </ListeTable.Column>
      </>
    ),
    [],
  );
  const renderHeader = React.useMemo(
    () => (
      <>
        {!modal && (
          <>
            <ListeTable.Column name="annee" sortable width={1}>
              Année
            </ListeTable.Column>
            <ListeTable.Column name="registre" sortable width={2}>
              Registre
            </ListeTable.Column>
          </>
        )}
        <ListeTable.Column name="numero" sortable width={modal ? 3 : 2}>
          Numéro de Pacs
        </ListeTable.Column>
        <ListeTable.Column name="statut" sortable width={1}>
          Statut
        </ListeTable.Column>
        <ListeTable.Column name="partenaire1" sortable width={modal ? 6 : 5}>
          Partenaire 1
        </ListeTable.Column>
        <ListeTable.Column name="partenaire2" sortable width={modal ? 6 : 5}>
          Partenaire 2
        </ListeTable.Column>
      </>
    ),
    [modal],
  );

  const formatItems = React.useMemo(
    () =>
      modal &&
      !stats &&
      _.flatMap(items, (item) => {
        const { conventionsDeclarations, ...rest } = item;
        const res = conventionsDeclarations?.map((convention) => ({
          ...rest,
          derniereConvention: convention,
          // Attention surcharge de l'identifiant pour le fonctionnement de la liste
          identifiant: convention.identifiant,
          identifiantPacs: item.identifiant,
        }));
        return res;
      }),
    [items, modal, stats],
  );

  const listItems = React.useMemo(() => {
    const list = (dashboard || !modal || stats ? items : formatItems) || [];
    if (assistant) {
      // @ts-ignore
      return [{ isAssistantEmptyRow: true, identifiant: 'empty-row' }, ...list];
    }
    return list;
  }, [items, dashboard, modal, stats, formatItems, assistant]);

  return (
    <>
      <ListeTableContainer
        // @ts-ignore
        items={listItems}
      >
        <ListeTable
          name={dataTestId}
          items={listItems}
          loading={loading}
          perPage={perPage}
          count={count}
          onLoad={onLoad}
          onSort={onSort}
          onSelect={handleSelect}
        >
          <ListeTable.Header>
            {assistant
              ? renderAssistantHeader
              : dashboard
                ? renderDashboardHeader
                : renderHeader}
          </ListeTable.Header>
          {renderRow as unknown as ReactNode}
        </ListeTable>
      </ListeTableContainer>
      {!_.isNil(pacsNonEnregistre) && (
        <DeclarationNonEnregistreeModal
          declarationType={selectedDeclarationType}
          numeroPacs={
            stats ? pacsNonEnregistre?.pacs?.numero : pacsNonEnregistre?.numero
          }
          onClose={() => setPacsNonEnregistre(null)}
          onOpenFiche={handleOpenFichePacs}
        />
      )}
    </>
  );
};

export default PacsListeTable;
